import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { Illustration, Text } from "@src/appV2/redesign/system/ui-components";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useRef } from "react";

import {
  buildWorkerOutcomeSteps,
  type InterviewExperienceResult,
  useUpdateInterview,
  type WorkerOutcomeResult,
} from "../../api/useUpdateInterview";
import { type FeedbackStep } from "./feedbackStep";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

interface OptionSelectionStepContentProps {
  options: Array<{
    label: string;
    value: WorkerOutcomeResult;
    textFieldHeading?: string;
    textFieldPlaceholder?: string;
  }>;
  heading: string;
  subtitle: string | undefined;
  interview: InterviewWithDetails;
  placementCandidate: PlacementCandidate;
  step: FeedbackStep;
  refetchInterviews: () => Promise<unknown>;
  isFetchingInterviews: boolean;
  onSubmit: (result: WorkerOutcomeResult) => void;
  selectedInterviewExperience?: InterviewExperienceResult;
  onFreeTextNeeded?: (
    result: WorkerOutcomeResult,
    textFieldHeading: string,
    textFieldPlaceholder?: string
  ) => void;
}

export function OptionSelectionStepContent(props: OptionSelectionStepContentProps) {
  const {
    options,
    heading,
    subtitle,
    interview,
    placementCandidate,
    step,
    refetchInterviews,
    isFetchingInterviews,
    selectedInterviewExperience,
    onSubmit,
    onFreeTextNeeded,
  } = props;

  const source = useInterviewAttendanceSource();
  const clickedOptionRef = useRef<WorkerOutcomeResult>();

  const { mutateAsync: updateInterview, isLoading: isUpdateInterviewLoading } =
    useUpdateInterview();

  const eventContext = {
    interview,
    placementCandidate,
    step,
    source,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_VIEWED, eventContext);

  async function handleOptionClick(option: (typeof options)[number]) {
    clickedOptionRef.current = option.value;
    if (isDefined(option.textFieldHeading) && onFreeTextNeeded) {
      onFreeTextNeeded(option.value, option.textFieldHeading, option.textFieldPlaceholder);
      return;
    }

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_CLICKED, {
      ...eventContext,
      action: option.value,
      hasMessage: false,
    });
    const steps = buildWorkerOutcomeSteps(selectedInterviewExperience, option.value);
    await updateInterview({
      interviewId: interview.id,
      attributes: {
        workerOutcome: {
          result: option.value,
          ...(steps ? { steps } : undefined),
        },
      },
    });
    await refetchInterviews();
    onSubmit(option.value);
  }

  return (
    <Stack
      spacing={8}
      alignItems="center"
      justifyContent="center"
      sx={{ paddingX: 8, paddingY: 6 }}
    >
      <Illustration type="facility" />
      <Stack spacing={6} textAlign="center" width="100%">
        <Text variant="h2">{heading}</Text>
        {subtitle && <Text>{subtitle}</Text>}
        <Stack spacing={4} width="100%">
          {options.map((option) =>
            isDefined(option.textFieldHeading) ? (
              <Button
                key={option.value}
                variant="outlined"
                size="large"
                disabled={isUpdateInterviewLoading || isFetchingInterviews}
                onClick={async () => {
                  await handleOptionClick(option);
                }}
              >
                {option.label}
              </Button>
            ) : (
              <LoadingButton
                key={option.value}
                variant="outlined"
                size="large"
                isLoading={isUpdateInterviewLoading && clickedOptionRef.current === option.value}
                disabled={isUpdateInterviewLoading || isFetchingInterviews}
                onClick={async () => {
                  await handleOptionClick(option);
                }}
              >
                {option.label}
              </LoadingButton>
            )
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
